import {
  type Context,
  type Dispatch,
  type MutableRefObject,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useFormikContext } from 'formik';
import type {
  EditNoteFormData,
  EditNoteFormDataValue,
} from '~/lib/types/notes/EditNoteFormData';
import type { NoteResponse } from '~/lib/types/notes/NoteResponse';
import { Model } from 'survey-core';
import {
  type AttachmentType,
  NoteStatus,
} from '~/lib/constants/AddPatientNoteModal.constants';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import {
  useGetNoteQuery,
  useLazyGetNoteTemplateAutopopulateDataQuery,
  useUpdateNoteMutation,
} from '~/store/services/notes/notes';
import { useInitialEditNoteFormValues } from '~/components/maud/patients/note/useInitialEditNoteFormValues';
import { useLatestNoteTemplateRevisionsWithLegacyHandling } from '~/components/maud/patients/note/useLatestNoteTemplateRevisionsWithLegacyHandling';
import { createSurveyJSModel } from '~/layouts/surveyJS/useSurveyJs';
import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';
import { type EditorV2State } from '@equip.health/ui';
import { NO_NOTE_TEMPLATE_OPTION } from '~/lib/constants/notes';
import { useAutosavePatientNote } from '~/components/maud/patients/note/useAutosavePatientNote';
import { DateTime } from 'luxon';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FieldErrorState } from '~/components/maud/patients/note/EditNoteForm';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { buildUpdateNoteRequest } from '~/lib/util/notes/noteUpdateRequests.util';
import { useCancelAppointmentMutation } from '~/store/services/appointment';
import { RecurringAppointmentUpdateOption } from '../constants/calendar.constants';
import { NoteType } from '../constants/NoteType';

const EMPTY_FIELD_ERRORS: FieldErrorState = {
  dateError: null,
  editorError: null,
  titleError: null,
  typeError: null,
  summaryError: null,
  carePlanError: null,
};

export type EditPatientNoteFormContextValue = {
  clearAutosaveDebounce: () => void;
  changeNoteTemplateRevision: (
    newRevisionId: string,
    callback: () => void,
  ) => void;
  closeNoteDrawerWithoutSaving: () => void;
  initialValues?: EditNoteFormData;
  isAutosaveNoteLoading: boolean;
  isLoading: boolean; // undefined before BE response
  isRefreshNoteTemplateRevisionDataFetching: boolean;
  isUpdateNoteLoading: boolean;
  lastSavedTime: DateTime | null;
  saveAndMinimize: () => void;
  note?: NoteResponse;
  noteTemplateFormModel?: Model;
  refreshNoteTemplateRevisionData: () => void;
  saveAndCloseNoteDrawer: () => void;
  setFormField: (
    fieldName: keyof EditNoteFormData,
    value: EditNoteFormDataValue,
  ) => void;
  setUploadedFiles: Dispatch<SetStateAction<AttachmentType[]>>;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  updateNote: (noteStatus: NoteStatus) => Promise<unknown>;
  uploadedFiles: AttachmentType[];
  autosaveError?: FetchBaseQueryError | SerializedError;
  resetAutosaveError: () => void;
  fieldErrors: FieldErrorState;
  setFieldErrors: Dispatch<SetStateAction<FieldErrorState>>;
  noteContentEditorRef: MutableRefObject<EditorV2State>;
  sessionSummaryEditorRef: MutableRefObject<EditorV2State>;
  sessionSummarySectionRef: MutableRefObject<HTMLDivElement | null>;
  carePlanSectionRef: MutableRefObject<HTMLDivElement | null>;
  updateFrequencyReadiness: (id: string, isReady: boolean) => void;
  removeFrequencyReadiness: (id: string) => void;
  checkCarePlanReadiness: () => boolean;
  showFrequencyErrors: boolean;
  setShowFrequencyErrors: (show: boolean) => void;
  resetFrequencyValidation: () => void;
};

export const EMPTY_EDIT_PATIENT_NOTE_FORM_CONTEXT: EditPatientNoteFormContextValue =
  {
    changeNoteTemplateRevision: () => {},
    checkCarePlanReadiness: () => true,
    clearAutosaveDebounce: () => {},
    closeNoteDrawerWithoutSaving: () => {},
    isAutosaveNoteLoading: false,
    isLoading: false,
    isRefreshNoteTemplateRevisionDataFetching: false,
    isUpdateNoteLoading: false,
    lastSavedTime: null,
    saveAndMinimize: () => {},
    refreshNoteTemplateRevisionData: () => {},
    saveAndCloseNoteDrawer: () => {},
    setFormField: () => {},
    setHasUnsavedChanges: () => {},
    setUploadedFiles: () => {},
    updateNote: () => Promise.resolve(),
    uploadedFiles: [],
    resetAutosaveError: () => {},
    fieldErrors: EMPTY_FIELD_ERRORS,
    setFieldErrors: () => {},
    noteContentEditorRef: { current: null },
    sessionSummaryEditorRef: { current: null },
    sessionSummarySectionRef: { current: null },
    carePlanSectionRef: { current: null },
    updateFrequencyReadiness: () => {},
    removeFrequencyReadiness: () => {},
    showFrequencyErrors: false,
    setShowFrequencyErrors: () => {},
    resetFrequencyValidation: () => {},
  };

export const EditPatientNoteFormContext: Context<EditPatientNoteFormContextValue> =
  createContext<EditPatientNoteFormContextValue>(
    EMPTY_EDIT_PATIENT_NOTE_FORM_CONTEXT,
  );

export const EditPatientNoteFormContextProvider = ({
  children,
}: PropsWithChildren) => {
  {
    const { currentUser } = useGlobalContext();
    const {
      sessionSummaryRichText: isSessionSummaryRichTextEnabled,
      cancelAppointmentMissedSessionNote:
        isCancelAppointmentMissedSessionNoteEnabled,
    } = useFlags<LDFlags>();
    const {
      isCreateNoteLoading,
      noteId,
      patientId,
      appointmentDetails,
      minimize,
      closeNoteDrawer,
    } = useEditNoteDrawerContext();
    const {
      values: formValues,
      setFieldValue,
      resetForm,
    } = useFormikContext<EditNoteFormData>();

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [lastSavedTime, setLastSavedTime] = useState<DateTime | null>(null);
    const [uploadedFiles, setUploadedFiles] = useState<AttachmentType[]>([]);
    const [
      openUploadInProgressWarningModal,
      setOpenUploadInProgressWarningModal,
    ] = useState<boolean>(false);
    const [noteTemplateFormModel, setNoteTemplateFormModel] = useState<Model>(
      new Model(),
    );
    const [fieldErrors, setFieldErrors] =
      useState<FieldErrorState>(EMPTY_FIELD_ERRORS);
    const [showFrequencyErrors, setShowFrequencyErrors] =
      useState<boolean>(false);
    const noteContentEditorRef = useRef<EditorV2State | null>(null);
    const sessionSummaryEditorRef = useRef<EditorV2State | null>(null);
    const sessionSummarySectionRef = useRef<HTMLDivElement | null>(null);
    const carePlanSectionRef = useRef<HTMLDivElement | null>(null);

    // Track ready state for each frequency: Map<id, isReady>
    const frequencyReadyStatesRef = useRef<Map<string, boolean>>(new Map());
    const [forceUpdate, setForceUpdate] = useState(0);

    const [cancelAppointment] = useCancelAppointmentMutation();

    const updateFrequencyReadiness = useCallback(
      (id: string, isReady: boolean) => {
        const previousValue = frequencyReadyStatesRef.current.get(id);
        frequencyReadyStatesRef.current.set(id, isReady);

        // Only trigger re-render if we're actively validating (banner is shown) AND value changed
        if (showFrequencyErrors && previousValue !== isReady) {
          setForceUpdate((prev) => prev + 1);
        }
      },
      [showFrequencyErrors],
    );

    const removeFrequencyReadiness = useCallback(
      (id: string) => {
        frequencyReadyStatesRef.current.delete(id);

        // Only trigger re-render if we're actively validating (banner is shown)
        if (showFrequencyErrors) {
          setForceUpdate((prev) => prev + 1);
        }
      },
      [showFrequencyErrors],
    );

    const resetFrequencyValidation = useCallback(() => {
      frequencyReadyStatesRef.current.clear();
      setShowFrequencyErrors(false);
      setForceUpdate(0);
    }, []);

    // Check if care plan is ready by reading the current Map state
    const checkCarePlanReadiness = useCallback(() => {
      if (frequencyReadyStatesRef.current.size === 0) return true;
      return Array.from(frequencyReadyStatesRef.current.values()).every(
        (ready) => ready,
      );
    }, []);

    // Auto-clear the validation banner when all frequencies become ready
    useEffect(() => {
      if (showFrequencyErrors && checkCarePlanReadiness()) {
        setShowFrequencyErrors(false);
        setFieldErrors((prev) => ({ ...prev, carePlanError: null }));
      }
    }, [showFrequencyErrors, forceUpdate, checkCarePlanReadiness]);

    const {
      data: getNoteData,
      isLoading: isGetNoteDataLoading,
      isSuccess: isGetNoteDataSuccess,
    } = useGetNoteQuery(noteId, {
      skip: !noteId,
    });

    const [updateNoteMutation, { isLoading: isUpdateNoteLoading }] =
      useUpdateNoteMutation();

    const [
      getNoteTemplateAutopopulateData,
      { isFetching: isRefreshNoteTemplateRevisionDataFetching },
    ] = useLazyGetNoteTemplateAutopopulateDataQuery();

    const { noteTemplateRevisions, isRevisionDataLoading, selectedRevision } =
      useLatestNoteTemplateRevisionsWithLegacyHandling(
        formValues.noteTemplateRevision?.node,
      );

    const {
      debouncedUpdateNote,
      isAutosaveNoteLoading,
      autosaveError,
      clearAutosaveDebounce,
      resetAutosaveError,
    } = useAutosavePatientNote({
      hasUnsavedChanges,
      isGetNoteDataSuccess,
      setHasUnsavedChanges,
      setLastSavedTime,
      noteId,
      uploadedFiles,
      formValues,
      noteTemplateFormModel,
      noteContentEditorRef,
      sessionSummaryEditorRef,
      patientId,
    });

    const onSurveyLoad = useCallback((surveyModel: Model) => {
      surveyModel.setDataCore(formValues.noteTemplateRevisionData);
    }, []);

    const onSurveyJsValueChanged = useCallback(() => {
      setHasUnsavedChanges(true);
      debouncedUpdateNote();
    }, [setHasUnsavedChanges]);

    useEffect(() => {
      if (selectedRevision) {
        setNoteTemplateFormModel(
          createSurveyJSModel({
            templateSchema: selectedRevision.templateJson,
            onSurveyLoad,
            onValueChanged: onSurveyJsValueChanged,
            jsonData: formValues.noteTemplateRevisionData,
          }),
        );
      }
    }, [
      selectedRevision,
      onSurveyLoad,
      onSurveyJsValueChanged,
      JSON.stringify(formValues.noteTemplateRevisionData),
    ]);

    const { initialValues } = useInitialEditNoteFormValues({
      note: getNoteData,
      setUploadedFiles,
      appointmentDetails,
    });

    useEffect(() => {
      resetForm({ values: initialValues });
    }, [initialValues]);

    const setFormField = (
      fieldName: keyof EditNoteFormData,
      value: EditNoteFormDataValue,
    ) => {
      setFieldValue(fieldName, value);
      setHasUnsavedChanges(true);
    };

    const updateNote = async (noteStatus: NoteStatus) => {
      const uploadedFilesMetadata = uploadedFiles.map((file) => ({
        attachmentExternalId: file.attachmentExternalId,
        fileName: file.fileName,
        statusCode: file.statusCode,
      }));

      const body = buildUpdateNoteRequest({
        appointmentExternalId: formValues?.appointment?.node,
        authoredByProviderExternalId: currentUser.userId,
        formValues,
        noteId,
        noteStatus,
        patientId,
        uploadedFiles: uploadedFilesMetadata,
        noteTemplateRevisionData: !!selectedRevision
          ? noteTemplateFormModel.data
          : null,
        noteContentEditorRef,
        sessionSummaryEditorRef: isSessionSummaryRichTextEnabled
          ? sessionSummaryEditorRef
          : null,
      });
      if (body) {
        const shouldCancelAppointment =
          !!body.appointmentExternalId &&
          (body.noteType === NoteType.MISSED_SESSION ||
            formValues?.noteTemplateRevision?.label?.toLocaleLowerCase() ===
              NoteType.MISSED_SESSION.toLocaleLowerCase()) &&
          body.noteStatus === NoteStatus.SIGNED;
        return updateNoteMutation({ body }).then(() => {
          setLastSavedTime(DateTime.now());
          setHasUnsavedChanges(false);

          if (
            shouldCancelAppointment &&
            isCancelAppointmentMissedSessionNoteEnabled
          ) {
            cancelAppointment({
              appointmentExternalId: body.appointmentExternalId,
              cancellationReason: 'NOSHOW',
              cancelType: RecurringAppointmentUpdateOption.ONLY,
            });
          }
        });
      }
      return Promise.resolve();
    };

    const saveAndMinimize = () => {
      if (hasUnsavedChanges) {
        updateNote(NoteStatus.DRAFT);
      }
      setFieldValue(
        'noteContentCache',
        noteContentEditorRef.current?.getHTML(),
      );
      if (isSessionSummaryRichTextEnabled) {
        setFieldValue(
          'sessionSummary',
          sessionSummaryEditorRef.current?.getHTML(),
        );
      }
      minimize();
    };

    const closeNoteDrawerAndResetState = () => {
      clearAutosaveDebounce();
      setHasUnsavedChanges(false);
      setLastSavedTime(null);
      closeNoteDrawer();
      setUploadedFiles([]);
      // this may not be working properly due to formik bug, or needs value props
      resetForm();
    };
    const saveAndCloseNoteDrawer = () => {
      if (isGetNoteDataSuccess) {
        updateNote(NoteStatus.DRAFT).then(() => {
          closeNoteDrawerAndResetState();
        });
      } else {
        closeNoteDrawerAndResetState();
      }
    };

    const checkInProgressFilesAndSaveAndCloseNoteDrawer = () => {
      const uploadInProgressFiles = (uploadedFiles ?? []).filter(
        (attachment: AttachmentType) => !attachment.attachmentExternalId,
      );
      if (uploadInProgressFiles.length) {
        setOpenUploadInProgressWarningModal(true);
      } else {
        saveAndCloseNoteDrawer();
      }
    };

    const refreshNoteTemplateRevisionData = () => {
      if (formValues.noteTemplateRevision?.node) {
        getNoteTemplateAutopopulateData({
          patientId,
          providerId: currentUser.userId,
          templateRevisionId: formValues.noteTemplateRevision.node,
          chartOnly: true,
        }).then((result) => {
          if (result.data) {
            noteTemplateFormModel.setDataCore({
              ...noteTemplateFormModel.data,
              ...result.data,
            });
          }
        });
      }
    };

    const changeNoteTemplateRevision = (
      newRevisionId: string,
      callback: () => void,
    ) => {
      const selectedTemplateRevision = noteTemplateRevisions.find(
        (ntr) => ntr.templateRevisionId === newRevisionId,
      );
      if (selectedTemplateRevision) {
        setFieldValue('noteContentCache', undefined);
        noteContentEditorRef.current?.commands.clearContent();
        setFieldValue('noteTemplateRevision', {
          label: selectedTemplateRevision.template?.templateTitle,
          node: selectedTemplateRevision.templateRevisionId,
        });
        if (!selectedTemplateRevision.template.showSessionSummary) {
          setFieldValue('sessionSummary', '');
          sessionSummaryEditorRef.current?.commands.clearContent();
        }

        getNoteTemplateAutopopulateData({
          patientId,
          providerId: currentUser.userId,
          templateRevisionId: selectedTemplateRevision.templateRevisionId,
        }).then((result) => {
          setFieldValue('noteTemplateRevisionData', result?.data || {});
          setHasUnsavedChanges(true);
          callback();
        });
      } else {
        setFieldValue('noteTemplateRevision', NO_NOTE_TEMPLATE_OPTION);
        setFieldValue('noteTemplateRevisionData', null);
        setFieldValue('sessionSummary', '');
        sessionSummaryEditorRef.current?.commands.clearContent();
        setHasUnsavedChanges(true);
        callback();
      }
    };

    return (
      <EditPatientNoteFormContext.Provider
        value={{
          autosaveError,
          changeNoteTemplateRevision,
          clearAutosaveDebounce,
          closeNoteDrawerWithoutSaving: closeNoteDrawerAndResetState,
          initialValues,
          isAutosaveNoteLoading,
          isLoading:
            isGetNoteDataLoading ||
            isCreateNoteLoading ||
            isRevisionDataLoading,
          isRefreshNoteTemplateRevisionDataFetching,
          isUpdateNoteLoading,
          lastSavedTime,
          saveAndMinimize,
          note:
            noteId !== getNoteData?.noteExternalId ? undefined : getNoteData,
          noteTemplateFormModel,
          noteContentEditorRef,
          sessionSummaryEditorRef,
          sessionSummarySectionRef,
          refreshNoteTemplateRevisionData,
          saveAndCloseNoteDrawer: checkInProgressFilesAndSaveAndCloseNoteDrawer,
          setFormField,
          setUploadedFiles,
          setHasUnsavedChanges,
          updateNote,
          uploadedFiles,
          resetAutosaveError,
          fieldErrors,
          setFieldErrors,
          carePlanSectionRef,
          updateFrequencyReadiness,
          removeFrequencyReadiness,
          checkCarePlanReadiness,
          showFrequencyErrors,
          setShowFrequencyErrors,
          resetFrequencyValidation,
        }}
      >
        <>
          {children}
          <DiscardChangesModalV2
            body="Your attachments are still uploading."
            isOpen={openUploadInProgressWarningModal}
            onClose={() => setOpenUploadInProgressWarningModal(false)}
            onConfirm={() => {
              setOpenUploadInProgressWarningModal(false);
              updateNote(NoteStatus.DRAFT).then(() => {
                saveAndCloseNoteDrawer();
              });
            }}
          />
        </>
      </EditPatientNoteFormContext.Provider>
    );
  }
};

export const useEditPatientNoteFormContext =
  (): EditPatientNoteFormContextValue => {
    const context = useContext(EditPatientNoteFormContext);
    if (!context) {
      throw new Error(
        'useEditPatientNoteFormContext must be used within a EditPatientNoteFormContextProvider.',
      );
    }
    return context;
  };
